import { useId, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ChevronRight, X } from 'lucide-react'
import { useConversation } from '@/api/queries'
import type { Source } from '@/api/types'
import { extractCitedIds } from '@/lib/citations'
import { riseIn, spring, stagger } from '@/lib/motion'
import { useMediaQuery, WIDE_QUERY } from '@/hooks/useMediaQuery'
import { conversationKey, selectTurn, useChatStore } from '@/stores/chatStore'
import { useUiStore } from '@/stores/uiStore'
import { Sheet } from '@/components/ui/Dialog'
import { IconButton } from '@/components/ui/IconButton'
import { SourceCard } from './SourceCard'

/** Resolve the selected assistant message from the live turn or the cached conversation. */
function useMessageSources(conversationId: string | null, messageKey: string | null) {
  const key = conversationKey(conversationId)
  const live = useChatStore((s) => {
    const turn = selectTurn(s, key)
    return turn && turn.assistant.clientId === messageKey ? turn.assistant : undefined
  })
  const clientKeys = useChatStore((s) => s.clientKeys)
  const { data } = useConversation(conversationId)

  return useMemo(() => {
    if (!messageKey) return null
    if (live) {
      return { sources: live.sources, cited: live.cited ?? extractCitedIds(live.content, live.sources) }
    }
    const message = data?.messages.find((m) => m.role === 'assistant' && (clientKeys[m.id] ?? m.id) === messageKey)
    if (!message) return null
    const sources = message.sources ?? []
    return { sources, cited: message.cited ?? extractCitedIds(message.content, sources) }
  }, [live, data, clientKeys, messageKey])
}

function PanelBody({ sources, cited, onClose }: { sources: Source[]; cited: string[]; onClose: () => void }) {
  const { sourceId, nonce } = useUiStore((s) => s.sourcesPanel)
  const [othersOpen, setOthersOpen] = useState(false)
  const othersId = useId()

  const byId = new Map(sources.map((s) => [s.id, s]))
  const citedSources = cited.map((id) => byId.get(id)).filter((s): s is Source => Boolean(s))
  const citedIds = new Set(citedSources.map((s) => s.id))
  const others = sources.filter((s) => !citedIds.has(s.id))
  const focusInOthers = Boolean(sourceId && others.some((s) => s.id === sourceId))
  const showOthers = othersOpen || focusInOthers

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-14 shrink-0 items-center justify-between gap-3 px-5">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold tracking-snug text-ink">Sources</h2>
          <p className="text-xs text-ink-faint">
            {citedSources.length} cited
            {others.length > 0 && ` · ${others.length} also consulted`}
          </p>
        </div>
        <IconButton label="Close sources" onClick={onClose}>
          <X />
        </IconButton>
      </div>

      <motion.div
        variants={stagger(0.06, 0.08)}
        initial="hidden"
        animate="show"
        className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 pb-6 pt-1"
      >
        {citedSources.length === 0 && (
          <p className="rounded-2xl bg-subtle/60 p-4 text-sm text-ink-muted">No sources were cited in this answer.</p>
        )}
        {citedSources.map((source) => (
          <motion.div key={source.id} variants={riseIn}>
            <SourceCard
              source={source}
              cited
              highlighted={source.id === sourceId}
              nonce={source.id === sourceId ? nonce : 0}
            />
          </motion.div>
        ))}

        {others.length > 0 && (
          <section className="pt-2">
            <button
              type="button"
              aria-expanded={showOthers}
              aria-controls={othersId}
              onClick={() => setOthersOpen(!showOthers)}
              className="pressable -ml-1.5 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-xs font-medium text-ink-muted hover:bg-subtle hover:text-ink"
            >
              <motion.span animate={{ rotate: showOthers ? 90 : 0 }} transition={spring.snappy} className="inline-flex">
                <ChevronRight className="size-3.5" aria-hidden />
              </motion.span>
              Also consulted ({others.length})
            </button>
            <AnimatePresence initial={false}>
              {showOthers && (
                <motion.div
                  id={othersId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={spring.smooth}
                  className="overflow-hidden"
                >
                  <div className="space-y-3 pt-3">
                    {others.map((source) => (
                      <SourceCard
                        key={source.id}
                        source={source}
                        cited={false}
                        highlighted={source.id === sourceId}
                        nonce={source.id === sourceId ? nonce : 0}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>
        )}
      </motion.div>
    </div>
  )
}

const PANEL_WIDTH = 420

/** Docked beside the chat on wide screens, a slide-over otherwise. */
export function SourcesPanel({ conversationId }: { conversationId: string | null }) {
  const { open, messageKey } = useUiStore((s) => s.sourcesPanel)
  const close = useUiStore((s) => s.closeSources)
  const wide = useMediaQuery(WIDE_QUERY)
  const data = useMessageSources(conversationId, messageKey)
  const visible = open && Boolean(data)

  if (wide) {
    return (
      <AnimatePresence initial={false}>
        {visible && data && (
          <motion.aside
            key="sources"
            aria-label="Sources"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: PANEL_WIDTH, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={spring.gentle}
            className="glass-panel relative shrink-0 overflow-hidden shadow-[inset_0.5px_0_0_rgb(var(--ink)/0.08)]"
          >
            {/* fixed inner width: the panel reveals, its content doesn't reflow */}
            <div className="flex h-full flex-col" style={{ width: PANEL_WIDTH }}>
              <PanelBody sources={data.sources} cited={data.cited} onClose={close} />
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    )
  }

  return (
    <Sheet
      open={visible}
      onOpenChange={(next) => !next && close()}
      side="right"
      title="Sources"
      showClose={false}
      className="w-[min(420px,100vw)]"
    >
      {data && <PanelBody sources={data.sources} cited={data.cited} onClose={close} />}
    </Sheet>
  )
}
